import { useSession } from '@descope/react-sdk';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getUserPreferences } from '@kirin/api-client/api/account';
import type { UserPreferencesResponse } from '@kirin/api-client/contracts/user-preferences';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Boot-time query for the authenticated user's preferences.
 *
 * Enabled only when the user is authenticated.
 * `retry: false` — failures fall back to localStorage via `ThemeProvider`,
 * surfaced inline via `PreferencesSyncNotice`, and reported to Sentry. No
 * silent retry path.
 */
export function usePreferencesQuery(): UseQueryResult<UserPreferencesResponse> {
  const { isAuthenticated } = useSession();
  return useQuery({
    queryKey: queryKeys.preferences.all,
    queryFn: () => getUserPreferences(apiClient),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    retry: false,
  });
}
